import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  SecondaryButton,
} from '../../components/chrome/Button.js';
import { MeSubpageShell } from '../../components/chrome/MeSubpageShell.js';
import { HistoryClock } from '../../components/history/HistoryClock.js';
import { ToolHistorySheet } from '../../components/history/HistorySheet.js';
import { useToolHistory } from '../../hooks/useToolHistory.js';
import { ApiError } from '../../lib/api-error.js';
import { PaymentSheet } from '../../components/payment/PaymentSheet.js';
import { SKU_CATALOG } from '@wds/astro-bot-shared/catalog';
import { useAuth } from '../../store/auth.js';
import { apiFetch } from '../../lib/api-client.js';
import { useSkuInvoice } from '../../hooks/useSkuInvoice.js';
import { HowItWorks } from '../../components/HowItWorks.js';
import { ReportMarkdown } from '../../components/ReportMarkdown.js';
import styles from './CheckForMe.module.css';

type View = 'input' | 'working' | 'result';

export function CheckForMe(): JSX.Element {
  const { t } = useTranslation(['flows', 'common']);
  const navigate = useNavigate();
  const jwt = useAuth((s) => s.jwt);
  const { purchase, redeemCoupon } = useSkuInvoice();
  const history = useToolHistory('check', false);
  const [showHistory, setShowHistory] = useState(false);
  const [resultId, setResultId] = useState<string | null>(null);
  const [isDiscussing, setIsDiscussing] = useState(false);
  const busyRef = useRef(false);
  const requestVersion = useRef(0);
  const [text, setText] = useState('');
  const [view, setView] = useState<View>('input');
  const [analysis, setAnalysis] = useState('');
  const [seedRef, setSeedRef] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  function clearResult(): void {
    requestVersion.current++;
    busyRef.current = false;
    setView('input');
    setText('');
    setAnalysis('');
    setSeedRef(null);
    setResultId(null);
    setError(null);
    setShowInvoice(false);
    setShowHistory(false);
    setIsDiscussing(false);
  }
  useEffect(() => {
    clearResult();
  }, [history.resultId, history.userId]);
  useEffect(() => {
    if (history.resultId === null || !history.detail.data) return;
    const saved = history.detail.data;
    setText(saved.sourceText as string);
    setAnalysis(saved.analysis as string);
    setResultId(saved.id as string);
    setView('result');
    setSeedRef(null);
  }, [history.resultId, history.detail.data]);
  useEffect(
    () => () => {
      requestVersion.current++;
    },
    [],
  );
  function startNew(): void {
    clearResult();
    history.open(null);
  }

  async function discuss(): Promise<void> {
    if (!jwt || busyRef.current) return;
    busyRef.current = true;
    setIsDiscussing(true);
    setError(null);
    const version = requestVersion.current;
    try {
      let ref = seedRef;
      if (resultId) {
        const response = await apiFetch(
          `/api/v1/history/check/${encodeURIComponent(resultId)}/seed`,
          jwt,
          { method: 'POST' },
        );
        if (!response.ok) throw new Error('seed_failed');
        ref = ((await response.json()) as { seedRef: string }).seedRef;
      }
      if (version !== requestVersion.current) return;
      if (!ref) throw new Error('seed_missing');
      navigate(`/chat?surface=cfm&seedRef=${encodeURIComponent(ref)}`);
    } catch {
      if (version === requestVersion.current) setError(t('check.error'));
    } finally {
      if (version === requestVersion.current) {
        busyRef.current = false;
        setIsDiscussing(false);
      }
    }
  }

  async function submit(): Promise<'ok' | 'paywall' | 'error'> {
    if (busyRef.current) return 'error';
    if (!jwt || text.trim().length < 20) {
      setError(t('check.tooShort'));
      return 'error';
    }
    busyRef.current = true;
    const version = requestVersion.current;
    setError(null);
    setSeedRef(null);
    setView('working');
    try {
      const res = await apiFetch('/api/v1/me/check-for-me', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (version !== requestVersion.current) return 'error';
      if (res.status === 402) {
        setView('input');
        return 'paywall';
      }
      if (res.status === 400) {
        setError(t('check.tooShort'));
        setView('input');
        return 'error';
      }
      if (res.status === 409) {
        setError(t('check.noChart'));
        setView('input');
        return 'error';
      }
      if (!res.ok) {
        setError(t('check.error'));
        setView('input');
        return 'error';
      }
      const data = (await res.json()) as {
        id?: string;
        analysis: string;
        seedRef?: string;
      };
      if (version !== requestVersion.current) return 'error';
      setAnalysis(data.analysis);
      setSeedRef(data.seedRef ?? null);
      setResultId(data.id ?? null);
      setView('result');
      if (data.id) history.open(data.id);
      return 'ok';
    } catch {
      if (version === requestVersion.current) {
        setError(t('check.error'));
        setView('input');
      }
      return 'error';
    } finally {
      if (version === requestVersion.current) busyRef.current = false;
    }
  }

  async function handleSubmitClick(): Promise<void> {
    const result = await submit();
    if (result === 'paywall') setShowInvoice(true);
  }

  async function doPurchase(): Promise<void> {
    setIsPaying(true);
    try {
      const { status } = await purchase('check_for_me_credit', {});
      if (status === 'paid') {
        setShowInvoice(false);
        // Give the bot webhook ~1s to process the payment and grant the Redis credit
        await new Promise((r) => setTimeout(r, 1000));
        await submit();
      }
    } catch {
      setError(t('check.error'));
    } finally {
      setIsPaying(false);
    }
  }

  async function doRedeemCoupon(code: string): Promise<void> {
    await redeemCoupon('check_for_me_credit', code, {});
    setShowInvoice(false);
    await new Promise((r) => setTimeout(r, 500));
    await submit();
  }

  return (
    <MeSubpageShell
      title={t('check.title')}
      data-testid="check-page"
      right={
        <HistoryClock
          label={t('history.open')}
          testId="check-history"
          disabled={view === 'working' || isDiscussing}
          onClick={() => setShowHistory(true)}
        />
      }
    >
      <div className={styles.body}>
        <HowItWorks toolKey="check" />
        {history.resultId !== null && history.detail.isPending ? (
          <p role="status">{t('common:states.loading')}</p>
        ) : history.resultId !== null && history.detail.isError ? (
          <div>
            <p role="alert">
              {t(
                history.detail.error instanceof ApiError &&
                  history.detail.error.status === 404
                  ? 'history.notFound'
                  : 'history.loadError',
              )}
            </p>
            <SecondaryButton onClick={() => void history.detail.refetch()}>
              {t('history.retry')}
            </SecondaryButton>
            <SecondaryButton onClick={startNew}>
              {t('history.new')}
            </SecondaryButton>
          </div>
        ) : (
          <>
            {view === 'working' && (
              <p data-testid="check-working" role="status">
                {t('check.working')}
              </p>
            )}

            {view === 'input' && (
              <>
                <p className={styles.prompt}>{t('check.prompt')}</p>
                <textarea
                  className={styles.textarea}
                  value={text}
                  onChange={(e) => {
                    if (history.resultId !== null) history.open(null, true);
                    setText(e.target.value);
                  }}
                  placeholder={t('check.placeholder')}
                  data-testid="check-textarea"
                />
                {error && (
                  <p className={styles.error} role="alert">
                    {error}
                  </p>
                )}
                <PrimaryButton
                  onClick={() => void handleSubmitClick()}
                  disabled={text.trim().length < 20}
                  data-testid="check-submit"
                >
                  {t('check.submit')}
                </PrimaryButton>
              </>
            )}

            {view === 'result' && (
              <>
                <div
                  className={styles.result}
                  data-testid="check-source"
                  style={{ overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
                >
                  <strong>{t('check.sourceText')}</strong>
                  <p>{text}</p>
                </div>
                {/* Same rendering as the saved result (TextResult): the analysis is markdown, its ** marks must not show. */}
                <div className={styles.result} data-testid="check-result">
                  <ReportMarkdown markdown={analysis} />
                </div>
                {(resultId || seedRef) && (
                  <PrimaryButton
                    onClick={() => void discuss()}
                    disabled={isDiscussing}
                    data-testid="check-discuss"
                  >
                    {t('check.discuss')}
                  </PrimaryButton>
                )}
                {error && (
                  <p className={styles.error} role="alert">
                    {error}
                  </p>
                )}
                <SecondaryButton
                  onClick={startNew}
                  disabled={isDiscussing}
                  data-testid="check-again"
                >
                  {t('check.checkAnother')}
                </SecondaryButton>
              </>
            )}
          </>
        )}
      </div>

      {showHistory && (
        <ToolHistorySheet
          kind="check"
          currentId={resultId}
          onClose={() => setShowHistory(false)}
          onOpen={(id) => {
            setShowHistory(false);
            history.open(id);
          }}
          onDeleted={(id) => {
            if (id === resultId || id === history.resultId) startNew();
          }}
        />
      )}

      {showInvoice && (
        <PaymentSheet
          skuCode="check_for_me_credit"
          title={t('check.buy', {
            stars: SKU_CATALOG['check_for_me_credit'].stars,
          })}
          priceStars={SKU_CATALOG['check_for_me_credit'].stars}
          bullets={[]}
          isPaying={isPaying}
          onPay={() => void doPurchase()}
          onCancel={() => setShowInvoice(false)}
          onApplyCoupon={doRedeemCoupon}
        />
      )}
    </MeSubpageShell>
  );
}
